import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mic, Sparkles, Volume2 } from "lucide-react";

function FloatBadge({ children, delay = 0, y = 4, className = "" }) {
    const reduce = useReducedMotion();
    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 14 }}
            whileInView={reduce ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
                duration: 0.7,
                ease: [0.2, 0.8, 0.2, 1],
                delay,
            }}
            animate={
                reduce
                    ? {}
                    : {
                          y: [0, -y, 0],
                      }
            }
            style={
                reduce
                    ? undefined
                    : {
                          animationTimingFunction: "ease-in-out",
                      }
            }
            className={className}
        >
            {children}
        </motion.div>
    );
}

export default function PhoneMockup() {
    const reduce = useReducedMotion();
    return (
        <div
            className="relative mx-auto w-full max-w-[380px] lg:max-w-[620px]"
            data-testid="hero-phone-mockup"
        >
            {/* Halo glow behind phone */}
            <div
                aria-hidden
                className="absolute -inset-10 rounded-[48px] blur-2xl opacity-80"
                style={{
                    background:
                        "radial-gradient(60% 60% at 50% 50%, rgba(48,140,138,0.18), rgba(48,140,138,0) 70%)",
                }}
            />

            {/* Phone shell */}
            <motion.div
                initial={reduce ? false : { opacity: 0, y: 28, rotate: -4 }}
                whileInView={reduce ? {} : { opacity: 1, y: 0, rotate: -4 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative mx-auto w-[280px] sm:w-[320px] lg:w-[340px] aspect-[9/19] rounded-[44px] bg-[var(--brand-ink)] p-[10px] shadow-[var(--shadow-drop)]"
                style={{ transformOrigin: "center" }}
            >
                {/* Screen */}
                <div className="relative h-full w-full rounded-[36px] bg-[var(--brand-paper-2)] overflow-hidden border border-white/10">
                    {/* Notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 h-[22px] w-[98px] rounded-full bg-[var(--brand-ink)] z-10" />

                    {/* Status bar */}
                    <div className="flex items-center justify-between px-6 pt-2.5 text-[10px] font-medium text-[var(--brand-ink)]">
                        <span>9:41</span>
                        <span className="opacity-70">•••</span>
                    </div>

                    {/* Content */}
                    <div className="px-4 pt-8 pb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-teal-2)] font-semibold">
                                    Shared map
                                </p>
                                <h4 className="mt-1 font-display text-[17px] leading-tight text-[var(--brand-ink)]">
                                    Dishes & late nights
                                </h4>
                            </div>
                            <div className="h-8 w-8 rounded-full bg-[rgba(48,140,138,0.12)] flex items-center justify-center">
                                <Sparkles
                                    size={15}
                                    className="text-[var(--brand-teal-2)]"
                                />
                            </div>
                        </div>

                        {/* Two columns (You / Them) */}
                        <div className="mt-4 grid grid-cols-2 gap-2.5">
                            <div className="rounded-[14px] bg-white border border-[var(--brand-border)] p-2.5">
                                <p className="text-[9px] uppercase tracking-[0.14em] text-[var(--brand-navy)] font-semibold">
                                    You
                                </p>
                                <ul className="mt-1.5 space-y-1 text-[10.5px] leading-snug text-[var(--brand-muted-2)]">
                                    <li>• Evenings feel loud</li>
                                    <li>• Want quieter weeknights</li>
                                    <li>• Okay with weekends lively</li>
                                </ul>
                            </div>
                            <div className="rounded-[14px] bg-white border border-[var(--brand-border)] p-2.5">
                                <p className="text-[9px] uppercase tracking-[0.14em] text-[var(--brand-teal-2)] font-semibold">
                                    Them
                                </p>
                                <ul className="mt-1.5 space-y-1 text-[10.5px] leading-snug text-[var(--brand-muted-2)]">
                                    <li>• Needs to unwind</li>
                                    <li>• Guests feel important</li>
                                    <li>• Open to heads-up</li>
                                </ul>
                            </div>
                        </div>

                        {/* Overlap pill */}
                        <div className="mt-3 flex items-center justify-center">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(48,140,138,0.12)] border border-[rgba(48,140,138,0.24)] px-3 py-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-teal-2)]" />
                                <span className="text-[10px] font-semibold tracking-[-0.01em] text-[var(--brand-teal-2)]">
                                    Overlap: quiet hours after 10pm
                                </span>
                            </div>
                        </div>

                        {/* Suggestion card */}
                        <div className="mt-3 rounded-[14px] bg-white border border-[var(--brand-border)] p-3 shadow-[0_6px_16px_rgba(14,27,42,0.06)]">
                            <div className="flex items-center gap-1.5">
                                <div className="h-4 w-4 rounded-md bg-[var(--brand-gold)]/20 flex items-center justify-center">
                                    <Sparkles
                                        size={10}
                                        className="text-[var(--brand-gold)]"
                                    />
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--brand-muted-2)] font-semibold">
                                    Here are a few paths forward
                                </p>
                            </div>
                            <ol className="mt-2 space-y-1.5 text-[11px] leading-snug text-[var(--brand-ink)]">
                                <li className="flex gap-2">
                                    <span className="text-[var(--brand-teal-2)] font-semibold">
                                        1.
                                    </span>
                                    Rotate dishes weekly, give a heads-up for
                                    guests.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[var(--brand-teal-2)] font-semibold">
                                        2.
                                    </span>
                                    Shared calendar for late-night plans.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[var(--brand-teal-2)] font-semibold">
                                        3.
                                    </span>
                                    Quiet-hours starting at 10pm on weeknights.
                                </li>
                            </ol>
                        </div>

                        {/* Voice input dock */}
                        <div className="mt-3 flex items-center justify-between rounded-[14px] bg-[var(--brand-ink)] text-[var(--brand-paper)] px-3 py-2">
                            <div className="flex items-center gap-2">
                                <span className="h-7 w-7 rounded-full bg-[var(--brand-teal)] flex items-center justify-center">
                                    <Mic size={12} />
                                </span>
                                <span className="text-[10.5px] font-medium">
                                    Tap to share your side
                                </span>
                            </div>
                            <span className="inline-flex items-center gap-0.5">
                                <Bar h={10} />
                                <Bar h={14} />
                                <Bar h={8} />
                                <Bar h={16} />
                                <Bar h={10} />
                                <Bar h={6} />
                                <Volume2
                                    size={11}
                                    className="ml-1 opacity-70"
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Floating card: summary */}
            <FloatBadge
                delay={0.2}
                y={5}
                className="hidden lg:block absolute -left-4 top-10 w-[220px] rounded-[16px] bg-white/95 backdrop-blur border border-brand shadow-[var(--shadow-lift)] p-3.5 z-20"
            >
                <div className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-md bg-[rgba(26,58,108,0.10)] flex items-center justify-center">
                        <Sparkles
                            size={12}
                            className="text-[var(--brand-navy)]"
                        />
                    </span>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-muted-2)] font-semibold">
                        Neutral summary
                    </p>
                </div>
                <p className="mt-1.5 font-display text-[14.5px] leading-snug text-[var(--brand-ink)]">
                    “Here’s what I’m hearing: you both want a calmer weeknight
                    rhythm.”
                </p>
            </FloatBadge>

            {/* Floating card: agreement */}
            <FloatBadge
                delay={0.45}
                y={4}
                className="hidden lg:block absolute -right-4 bottom-14 w-[220px] rounded-[16px] bg-white/95 backdrop-blur border border-brand shadow-[var(--shadow-lift)] p-3.5 z-20"
            >
                <div className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-md bg-[rgba(48,140,138,0.14)] flex items-center justify-center">
                        <svg
                            width="11"
                            height="11"
                            viewBox="0 0 20 20"
                            fill="none"
                            aria-hidden
                        >
                            <path
                                d="M4 10.5 8 14.5 16 6"
                                stroke="#2A7B79"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-muted-2)] font-semibold">
                        Agreement draft
                    </p>
                </div>
                <ul className="mt-2 space-y-1.5 text-[11.5px] text-[var(--brand-ink)]">
                    <li className="flex items-start gap-2">
                        <span className="mt-1 h-3 w-3 rounded-[4px] border border-[var(--brand-teal-2)] bg-[rgba(48,140,138,0.12)]" />
                        Quiet hours start at 10pm on weeknights
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="mt-1 h-3 w-3 rounded-[4px] border border-[var(--brand-teal-2)] bg-[rgba(48,140,138,0.12)]" />
                        Guests get a same-day heads-up
                    </li>
                </ul>
            </FloatBadge>
        </div>
    );
}

function Bar({ h = 10 }) {
    return (
        <span
            className="inline-block w-[2.5px] rounded-full bg-[var(--brand-teal-3)]/80"
            style={{ height: h }}
        />
    );
}
